import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import type { RetailOrderLineItem } from '~/api/graphql/generated/types'
import { getEditingChargesMap } from '~/store/duck/clientFinanceData'
import type { InvoiceLineItem } from '~/types'

/**
 * Optimistic override for an item's editable fields
 */
export type OptimisticOverride = {
  declined?: boolean
  deleted?: boolean
  producerId?: string
  quantity?: number
}

/**
 * Type guard to check if an item is an InvoiceLineItem with children
 */
const isInvoiceLineItemWithChildren = (
  item: InvoiceLineItem | RetailOrderLineItem,
): item is InvoiceLineItem & { items: InvoiceLineItem[] } =>
  'items' in item && Array.isArray(item.items) && item.items.length > 0

/**
 * Hook for managing optimistic updates to charge sheet items
 * Provides immediate UI feedback before server confirmation
 *
 * @param items - The current list of items from the server
 * @returns Object with optimisticItems array and applyOptimistic callback
 */
export const useOptimisticItemUpdates = (
  items: Array<InvoiceLineItem | RetailOrderLineItem>,
) => {
  // Map of item ID to optimistic override
  const [optimisticMap, setOptimisticMap] = useState<
    Map<string, OptimisticOverride>
  >(new Map())

  // Track previous editingChargesMap to detect when edits complete
  const previousEditingMapRef = useRef<Record<string, boolean>>({})
  const editingChargesMap = useSelector(getEditingChargesMap)

  /**
   * Apply optimistic update for an item
   * Merges new changes with any existing optimistic overrides
   */
  const applyOptimistic = (itemId: string, changes: OptimisticOverride) => {
    setOptimisticMap((prev) => {
      const next = new Map(prev)
      const existing = prev.get(itemId)

      // Merge new changes with any existing optimistic changes
      const optimistic = {
        ...existing,
        ...changes,
      }

      next.set(itemId, optimistic)
      return next
    })
  }

  /**
   * Detect when an edit completes (success or failure)
   * Clears optimistic state so server data takes over
   */
  useEffect(() => {
    const previousMap = previousEditingMapRef.current
    const currentMap = editingChargesMap

    // Find items that were removed from the map (for deletes)
    // Don't clear for items set to false (edits waiting for refetch)
    const completedIds = Object.keys(previousMap).filter(
      (id) => previousMap[id] && !(id in currentMap),
    )

    if (completedIds.length > 0) {
      setOptimisticMap((prev) => {
        const next = new Map(prev)
        completedIds.forEach((id) => {
          // Clear the optimistic state for deleted items
          // For edits (value set to false), keep optimistic state until refetch
          next.delete(id)
        })
        return next
      })
    }

    previousEditingMapRef.current = currentMap
  }, [editingChargesMap])

  /**
   * Recursively applies optimistic overrides to items and their children
   */
  const applyOptimisticRecursive = useCallback(
    (
      itemsList: Array<InvoiceLineItem | RetailOrderLineItem>,
    ): Array<InvoiceLineItem | RetailOrderLineItem> =>
      itemsList
        .map((item) => {
          const override = optimisticMap.get(item.id)
          let updatedItem = item

          // Apply optimistic overrides to the current item
          if (override) {
            updatedItem = {
              ...item,
              ...override,
            }
          }

          // Recursively apply optimistic overrides to children
          if (isInvoiceLineItemWithChildren(updatedItem)) {
            updatedItem = {
              ...updatedItem,
              items: applyOptimisticRecursive(updatedItem.items),
            } as InvoiceLineItem
          }

          return updatedItem
        })
        .filter((item) => {
          const override = optimisticMap.get(item.id)
          return !override?.deleted
        }),
    [optimisticMap],
  )

  /**
   * Merge server items with optimistic overrides
   * Filter out items marked as deleted
   */
  const optimisticItems = useMemo(
    () => applyOptimisticRecursive(items),
    [items, applyOptimisticRecursive],
  )

  return {
    optimisticItems,
    applyOptimistic,
  }
}
